import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { fmtDateTimeParis } from '../../lib/api';
import { AlertTriangle, RefreshCw, FileText, ChevronDown } from 'lucide-react';

// 14/10 — LE RAPPORT DU BOT, TOUT EN HAUT DE LA PAGE « Bot Telegram ».
// Marlon : « tu vas me mettre même le rapport dans la partie Bot Telegram, et je pourrais voir ça
// directement tout en haut ». Brief de la session bot : BRIEF_CHATHUB_RAPPORT_BOT_14-10.md.
//
// LECTURE SEULE. On lit `GET /api/rapport` du bot par le relais authentifié de ChatHub
// (`/api/bot-panel/rapport`, même chemin que les autres écrans du bot). Le bot le recalcule lui-même
// toutes les 30 min ; on n'appelle JAMAIS `?frais=1` ici (≈ 2 min de calcul). Aucun bouton pour
// sortir un fan : la sortie reste une décision de Marlon, appliquée par la session bot.

const eur = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const usd = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
const aSortir = (p) => String(p || '').toLowerCase().startsWith('à sortir');

function Proposition({ texte }) {
  if (aSortir(texte)) {
    return <span className="inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-500/20 text-red-200 border border-red-500/40">{texte}</span>;
  }
  if (String(texte || '').startsWith('inactif')) return <span className="text-[11px] text-slate-500">{texte}</span>;
  return <span className="text-[11px] text-emerald-300/90">{texte || '—'}</span>;
}

export default function RapportBot() {
  const [r, setR] = useState(null);
  const [erreur, setErreur] = useState('');
  const [modele, setModele] = useState('');
  const [charge, setCharge] = useState(false);
  // 14/10 (Marlon : « je dois faire un déroulé pour voir ça ») : replié par défaut.
  const [ouvert, setOuvert] = useState(false);

  const lire = useCallback(() => {
    setCharge(true);
    api.get('/bot-panel/rapport')
      .then(({ data }) => { setR(data); setErreur(''); })
      .catch((e) => setErreur(e?.response?.data?.detail || e.message || 'rapport indisponible'))
      .finally(() => setCharge(false));
  }, []);
  useEffect(() => { lire(); }, [lire]);

  const modeles = useMemo(() => (r && r.modeles) || [], [r]);
  useEffect(() => {
    if (!modele && modeles.length) {
      // On ouvre sur le modèle qui a le plus d'alertes (sinon le premier).
      const tri = [...modeles].sort((a, b) => (b.alertes || 0) - (a.alertes || 0));
      setModele(tri[0].nom);
    }
  }, [modeles, modele]);

  if (erreur && !r) {
    return (
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-5 mb-4 text-[13px] text-amber-100/90" data-testid="rapport-bot">
        Rapport du bot indisponible pour l'instant ({erreur}). <button onClick={lire} className="underline">Réessayer</button>
      </div>
    );
  }
  if (!r) return <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4 text-sm text-slate-500" data-testid="rapport-bot">Chargement du rapport du bot…</div>;

  const alertes = modeles.flatMap((m) => (m.fans || []).flatMap((f) => (f.alertes || []).map((t) => ({ m: m.nom, f, t }))));
  const courant = modeles.find((m) => m.nom === modele) || modeles[0] || { fans: [] };
  const fans = courant.fans || [];

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4" data-testid="rapport-bot">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => setOuvert((v) => !v)} className="flex-1 min-w-0 text-left flex items-center gap-2" data-testid="rapport-bot-derouler">
          <FileText size={16} className="text-indigo-400 shrink-0" />
          <span className="font-semibold text-white">Rapport du bot</span>
          <span className="text-[12px] text-slate-400 truncate">
            {modeles.map((m) => `${m.nom} ${m.nbFans} fans`).join(' · ')}
            {alertes.length > 0 && <span className="text-red-300"> · {alertes.length} alerte{alertes.length > 1 ? 's' : ''}</span>}
          </span>
          <ChevronDown size={18} className={`text-slate-400 shrink-0 ml-auto transition-transform ${ouvert ? 'rotate-180' : ''}`} />
        </button>
        {ouvert && (
          <button onClick={lire} disabled={charge} className="text-[12px] text-slate-400 hover:text-white flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={13} className={charge ? 'animate-spin' : ''} /> relire
          </button>
        )}
      </div>
      {ouvert && (<div className="mt-3">
      <p className="text-[12px] text-slate-400 mb-3" data-testid="rapport-bot-heure">
        Calculé le <b className="text-slate-200">{fmtDateTimeParis(r.genereA)}</b> (heure de Paris)
        {r.calculeIlYaMin != null && <> — il y a <b className="text-slate-200">{r.calculeIlYaMin} min</b></>}.
        Le bot le recalcule tout seul toutes les 30 min.
      </p>

      {/* 1. Une ligne par modèle */}
      <div className="overflow-x-auto mb-3">
        <table className="w-full text-[12px] min-w-[620px]" data-testid="rapport-bot-modeles">
          <thead><tr className="text-left text-slate-500">
            <th className="py-1 pr-3 font-medium">Modèle</th>
            <th className="py-1 pr-3 font-medium">Fans en zone bot</th>
            <th className="py-1 pr-3 font-medium">Coût 24 h</th>
            <th className="py-1 pr-3 font-medium">dont fans qui n'ont jamais payé</th>
            <th className="py-1 pr-3 font-medium">« à sortir ? »</th>
            <th className="py-1 pr-3 font-medium">Alertes</th>
          </tr></thead>
          <tbody>
            {modeles.map((m) => (
              <tr key={m.nom} className="border-t border-white/[0.06] text-slate-200">
                <td className="py-1.5 pr-3 font-semibold text-white">{m.nom}</td>
                <td className="py-1.5 pr-3">{m.nbFans}</td>
                <td className="py-1.5 pr-3">{usd(m.cout24h)}</td>
                <td className="py-1.5 pr-3">{usd(m.coutJamaisPaye)}</td>
                <td className={`py-1.5 pr-3 ${m.aSortir ? 'text-red-300 font-semibold' : ''}`}>{m.aSortir}</td>
                <td className={`py-1.5 pr-3 ${m.alertes ? 'text-red-300 font-semibold' : ''}`}>{m.alertes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        Coût <b className="text-slate-300">ESTIMÉ</b> = messages du bot × {String(r.prixMessage ?? '?').replace('.', ',')} $ (coût réel Anthropic ÷ messages, mesuré le 14/10).
        Ce n'est pas la facture : elle se lit dans la console Anthropic.
      </p>

      {/* 2. Les alertes d'abord, en rouge */}
      {alertes.length > 0 && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/[0.07] p-3 mb-4" data-testid="rapport-bot-alertes">
          <div className="text-[13px] font-semibold text-red-200 flex items-center gap-2 mb-2"><AlertTriangle size={14} /> {alertes.length} alerte{alertes.length > 1 ? 's' : ''}</div>
          <ul className="space-y-1 text-[12px] text-red-100/90">
            {alertes.map((a, i) => (
              <li key={i}><span className="text-red-300/80">{a.m}</span> · <b className="text-white">{a.f.nom}</b> — {a.t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 3. Le tableau des fans, trié par coût (ordre du bot, gardé tel quel) */}
      <div className="flex items-center gap-1 mb-2 flex-wrap">
        {modeles.map((m) => (
          <button key={m.nom} onClick={() => setModele(m.nom)}
            className={`px-3 py-1 rounded-lg text-[12px] border ${courant.nom === m.nom ? 'border-indigo-500 bg-indigo-500/15 text-white' : 'border-white/10 text-slate-400 hover:text-white'}`}>
            {m.nom} ({m.nbFans})
          </button>
        ))}
        <span className="text-[11px] text-slate-500 ml-1">trié par coût sur 24 h, du plus cher au moins cher</span>
      </div>
      {/* Tous les fans, sans coupure (une coupure pourrait cacher un « à sortir ? ») : la zone défile. */}
      <div className="overflow-auto max-h-[480px] rounded-lg border border-white/[0.06]">
        <table className="w-full text-[12px] min-w-[1050px]" data-testid="rapport-bot-fans">
          <thead className="sticky top-0 bg-[#0d0d12]"><tr className="text-left text-slate-500 align-bottom">
            <th className="py-1 pr-3 font-medium">Fan</th>
            <th className="py-1 pr-3 font-medium">Dépensé</th>
            <th className="py-1 pr-3 font-medium">Situation</th>
            <th className="py-1 pr-3 font-medium whitespace-nowrap">Messages 24 h<br /><span className="text-slate-600">lui / elle</span></th>
            <th className="py-1 pr-3 font-medium">Coût 24 h</th>
            <th className="py-1 pr-3 font-medium">Liens 24 h</th>
            <th className="py-1 pr-3 font-medium">Ce qui a été fait</th>
            <th className="py-1 pr-3 font-medium">Prochaine étape du bot (quand)</th>
            <th className="py-1 pr-3 font-medium">Proposition</th>
          </tr></thead>
          <tbody>
            {fans.map((f) => (
              <tr key={f.tg} className={`border-t border-white/[0.06] align-top ${aSortir(f.proposition) ? 'bg-red-500/[0.06]' : ''}`}>
                <td className="py-1.5 pr-3 text-white max-w-[180px]">{f.nom}{(f.alertes || []).length > 0 && <span className="text-red-300" title={(f.alertes || []).join(' · ')}> ⚠</span>}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-slate-200">{eur(f.depense)}</td>
                <td className="py-1.5 pr-3 text-slate-300 max-w-[200px]">{f.situation || '—'}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-slate-300">{f.msgsLui ?? 0} / {f.msgsElle ?? 0}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-slate-200">{usd(f.cout)}</td>
                <td className="py-1.5 pr-3 text-slate-300">{f.liens24h ?? 0}</td>
                <td className="py-1.5 pr-3 text-slate-400 max-w-[170px]">{f.fait || '—'}</td>
                <td className="py-1.5 pr-3 text-slate-300 max-w-[260px]">{f.prochaine || '—'}</td>
                <td className="py-1.5 pr-3 max-w-[170px]"><Proposition texte={f.proposition} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">{fans.length} fan{fans.length > 1 ? 's' : ''} de {courant.nom} · les lignes en rouge sont proposées « à sortir ? » — la décision reste la tienne.</p>
      </div>)}
    </div>
  );
}
